import React from 'react';
import { useNavigate } from 'react-router-dom';

interface SubscriptionModalProps {
  darkMode: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ 
  darkMode, 
  onClose, 
  onUpgrade 
}) => {
  // Custom CSS for hiding scrollbar
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .scrollbar-hide {
        -ms-overflow-style: none;  /* Internet Explorer 10+ */
        scrollbar-width: none;  /* Firefox */
      }
      .scrollbar-hide::-webkit-scrollbar { 
        display: none;  /* Safari and Chrome */
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ESC key handler
  React.useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);


  const navigate = useNavigate();

  const handleUpgrade = (plan: 'monthly' | 'annual' | 'trial') => {
    navigate(`/checkout?plan=${plan}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${darkMode ? 'bg-gray-900' : 'bg-white'} rounded-xl shadow-sm max-w-6xl w-full max-h-[95vh] border ${darkMode ? 'border-gray-700' : 'border-slate-200'} relative`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 z-50 w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
            darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
          }`}
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="max-h-[95vh] overflow-y-auto rounded-xl scrollbar-hide">
          <div className="p-6 sm:p-8 lg:p-10 lg:pr-14">
          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-slate-700' : 'bg-blue-100'}`}>
              <svg className={`w-7 h-7 ${darkMode ? 'text-slate-300' : 'text-blue-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h1 className={`text-2xl sm:text-3xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Unlock Your Full Potential
            </h1>
            <p className={`text-sm sm:text-base max-w-2xl mx-auto ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              Join thousands of successful real estate professionals who've transformed their careers with our study platform.{' '}
              <span className={darkMode ? 'text-blue-400 font-medium' : 'text-blue-600 font-medium'}>Upgrade today and never look back.</span>
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-8 max-w-4xl mx-auto">
            {/* Free Trial */}
            <div className={`flex flex-col rounded-xl p-6 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} shadow-sm`}>
              <div className="text-center mb-5">
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Free Trial</h3>
                <div className="mb-1">
                  <span className={`text-3xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>$0</span>
                  <span className={`text-sm ml-1.5 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>/3 days</span>
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Then $99/year · Cancel anytime during trial</p>
              </div>
              <ul className={`space-y-2.5 mb-5 flex-1 text-sm ${darkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                {['All 500+ premium questions', 'Detailed explanations & insights', 'Advanced progress tracking', 'Study analytics & reports', 'Mobile & desktop access', 'Cancel anytime during trial'].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${darkMode ? 'bg-emerald-900/60 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade('trial')}
                className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg font-semibold text-sm hover:bg-emerald-700 transition-colors mt-auto"
              >
                Start Free Trial
              </button>
            </div>

            {/* Annual Plan */}
            <div className={`flex flex-col rounded-xl p-6 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'} shadow-sm`}>
              <div className="text-center mb-5">
                <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-medium mb-2 ${darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>Best value</span>
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Annual Plan</h3>
                <div className="mb-1">
                  <span className={`text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>$99</span>
                  <span className={`text-sm ml-1.5 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>/year</span>
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Full access, best price</p>
              </div>
              <ul className={`space-y-2.5 mb-5 flex-1 text-sm ${darkMode ? 'text-gray-300' : 'text-slate-700'}`}>
                {['All 500+ premium questions', 'Priority customer support', 'Advanced analytics dashboard', 'Custom study plans', 'Early access to new content', 'Exclusive study materials'].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${darkMode ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade('annual')}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors mt-auto"
              >
                Start Annual Plan
              </button>
            </div>
          </div>

          {/* Features */}
          <div className={`mb-8 p-6 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Premium features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { icon: '📚', title: '500+ Premium Questions', desc: 'Comprehensive coverage of real estate principles' },
                { icon: '🎯', title: 'AI-Powered Learning', desc: 'Adaptive study algorithm for optimal retention' },
                { icon: '📊', title: 'Advanced Analytics', desc: 'Detailed insights into your study progress' },
                { icon: '📱', title: 'Cross-Platform Access', desc: 'Study anywhere on any device' },
                { icon: '🔄', title: 'Unlimited Sessions', desc: 'Practice as much as you need' },
                { icon: '🎨', title: 'Premium UI/UX', desc: 'Beautiful, intuitive interface design' }
              ].map((feature, index) => (
                <div key={index} className={`text-center p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-slate-200'}`}>
                  <div className="text-2xl mb-2">{feature.icon}</div>
                  <h4 className={`font-semibold mb-1 text-sm ${darkMode ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h4>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trust */}
          <div className={`mb-8 p-6 rounded-xl border text-center ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Trusted by thousands
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div>
                <div className={`text-xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>10K+</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Active Users</div>
              </div>
              <div>
                <div className={`text-xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>95%</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Satisfaction</div>
              </div>
              <div>
                <div className={`text-xl font-bold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>4.9/5</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>Rating</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-emerald-900/60 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <span className={darkMode ? 'text-gray-400' : 'text-slate-500'}>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                </div>
                <span className={darkMode ? 'text-gray-400' : 'text-slate-500'}>Bank-level security</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button
              onClick={onClose}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Maybe Later
            </button>
            <button
              onClick={() => handleUpgrade('trial')}
              className="flex-1 bg-slate-800 text-white py-3 px-4 rounded-lg font-semibold hover:bg-slate-700 transition-colors"
            >
              Start Free Trial
            </button>
          </div>

          <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
            Join professionals who've transformed their careers.{' '}
            <span className={darkMode ? 'text-blue-400 font-medium' : 'text-blue-600 font-medium'}>Your success starts here.</span>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;

